import { useEffect, useState } from "react";
import type { Seconds } from "@rosette/ts/src/util/units.js";
import type { FormatDateOptions } from "@rosette/ts/src/util/formatting.js";
import { formatDate } from "@rosette/ts/src/util/formatting.js";


const serverSideFormatter = new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "long", year: "numeric",
});


/**
 * Built to always format the same way on the server and the client, to
 * avoid hydration issues. The client can then later update its formatting.
 */
function doServerSideFormat(date: Date, options?: FormatDateOptions) {
    const dateStr = serverSideFormatter.format(date);
    if (options?.verb)
        return `${options?.verb} on ${dateStr}`;

    return dateStr;
}


/**
 * Formats a date using language such as "10 hours ago" when
 * a date is very recent. Schedules timeouts to update this
 * formatting as time progresses.
 */
export function useFormatDate(
    date: Date | null | undefined,
    options?: FormatDateOptions,
): string {

    const [dateStr, setDateStr] = useState(
        date ? doServerSideFormat(date, options) : "",
    );
    useEffect(() => {
        if (!date)
            return;

        let timeoutID: number | NodeJS.Timeout | null = null;

        const updateDateStr = () => {
            let seconds: Seconds | null = null;
            setDateStr(formatDate(
                date, options,
                (timeout: Seconds) => {
                    seconds = timeout;
                },
            ));
            if (seconds !== null) {
                timeoutID = setTimeout(updateDateStr, 100 + 1000 * seconds);
            }
        };

        updateDateStr();
        return () => {
            if (timeoutID !== null) {
                clearTimeout(timeoutID);
            }
        };
    }, [date, options]);

    return dateStr;
}
